// ===========================================================================
// PatientDetailCard — tarjeta de detalle del paciente YA seleccionado.
//
// Se muestra DEBAJO del paciente seleccionado en PatientSearch. Presenta tres
// tabs:
//   1. Demographics           → datos de la ficha (sin llamada extra)
//   2. Care Gaps (this year)  → care gaps del año en curso (CareGapsV2)
//   3. Coding Opportunities   → coding opportunities del año en curso
//
// La cabecera de los tabs de Care Gaps / Coding Opportunities muestra la
// CANTIDAD (badge). Si el paciente está inactivo (status !== 1) la tarjeta
// completa toma un tono rojizo de aviso.
//
// Self-contained: recibe el `patient` por prop y resuelve sus propias llamadas.
// ===========================================================================
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Tabs,
  Tab,
  Badge,
  Chip,
  Typography,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

import {
  getPatientCareGaps,
  getPatientCodingOpportunities,
  getPatientMemberships,
  currentYear,
} from "../../services/patientDetailService";
import { formatDate, safe } from "../../utils/format";

/** ¿El paciente está activo? Mismo criterio que el Patient Map roster. */
const isPatientActive = (patient) => Number(patient?.status) === 1;

// --- Helpers de presentación -----------------------------------------------

function Field({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  );
}

/** Estado común de carga/vacío/error para las listas de los tabs. */
function ListState({ loading, error, isEmpty, emptyText, children }) {
  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={26} />
      </Stack>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        {error}
      </Alert>
    );
  }
  if (isEmpty) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 4, color: "text.secondary" }}>
        <CheckCircleOutlineIcon color="success" />
        <Typography variant="body2">{emptyText}</Typography>
      </Stack>
    );
  }
  return children;
}

// --- Tabs de demografía ------------------------------------------------------

function DemographicsTab({ patient }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
        gap: 1.75,
      }}
    >
      <Field label="Member ID" value={safe(patient.memberId)} />
      <Field label="Member type" value={safe(patient.memberType)} />
      <Field label="CIP" value={safe(patient.cipId)} />
      <Field label="Date of birth" value={formatDate(patient.dateOfBirth)} />
      <Field label="Age" value={safe(patient.age)} />
      <Field label="Gender" value={safe(patient.gender)} />
      <Field label="Phone" value={safe(patient.phoneNumber)} />
      <Field label="Mobile" value={safe(patient.mobileNumber)} />
      <Field label="Email" value={safe(patient.email)} />
      <Field label="PCP" value={`${safe(patient.pcpName)}${patient.pcpTitle ? ` (${patient.pcpTitle})` : ""}`} />
      <Field label="Medical center" value={safe(patient.medicalCenter)} />
      <Field label="Location" value={safe(patient.locationAddress)} />
    </Box>
  );
}

// --- Tab de care gaps --------------------------------------------------------

function CareGapsTab({ state, year }) {
  return (
    <ListState
      loading={state.loading}
      error={state.error}
      isEmpty={state.items.length === 0}
      emptyText={`No care gaps for ${year}.`}
    >
      <Stack spacing={1}>
        {state.items.map((cg) => (
          <Paper key={cg.careGapId} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <AssignmentLateOutlinedIcon fontSize="small" color="warning" />
              <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                {safe(cg.careGapName)}
              </Typography>
              <Chip
                size="small"
                label={safe(cg.careGapStatusName)}
                color={cg.careGapStatusKey === "closed" ? "success" : "default"}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.66rem" }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {safe(cg.workflowStatusName)}
              {cg.hedisCategory ? ` · ${cg.hedisCategory}` : ""}
              {cg.historicallyNonCompliant ? " · Historically non-compliant" : ""}
            </Typography>
          </Paper>
        ))}
      </Stack>
    </ListState>
  );
}

// --- Tab de coding opportunities --------------------------------------------

function CodingOppsTab({ state }) {
  return (
    <ListState
      loading={state.loading}
      error={state.error}
      isEmpty={state.items.length === 0}
      emptyText="No coding opportunities."
    >
      <Stack spacing={1}>
        {state.items.map((op) => (
          <Paper key={op.codingOpportunityId} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <LocalHospitalOutlinedIcon fontSize="small" color="info" />
              <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                {safe(op.hccId)} · {safe(op.hccDescription)}
              </Typography>
              {op.measureYear && (
                <Chip
                  size="small"
                  label={`MY ${op.measureYear}`}
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.66rem" }}
                />
              )}
              <Chip
                size="small"
                label={safe(op.statusName)}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.66rem" }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {safe(op.diagnosisCode)} · {safe(op.diagnosisDescription)}
              {op.lastDos ? ` · Last DOS ${formatDate(op.lastDos)}` : ""}
            </Typography>
          </Paper>
        ))}
      </Stack>
    </ListState>
  );
}

// --- Tab de membresías -------------------------------------------------------

function MembershipTab({ state }) {
  return (
    <ListState
      loading={state.loading}
      error={state.error}
      isEmpty={state.items.length === 0}
      emptyText="No memberships found."
    >
      <Stack spacing={1}>
        {state.items.map((m) => (
          <Paper key={m.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <BadgeOutlinedIcon fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                {safe(m.plan)}
              </Typography>
              {m.memberType && (
                <Chip
                  size="small"
                  label={m.memberType}
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.66rem" }}
                />
              )}
              <Chip
                size="small"
                label={m.statusLabel}
                color={m.isActive ? "success" : "default"}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.66rem" }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {safe(m.network)}
              {m.memberId ? ` · Member ID ${m.memberId}` : ""}
              {m.effectiveDate ? ` · Effective ${formatDate(m.effectiveDate)}` : ""}
            </Typography>
          </Paper>
        ))}
      </Stack>
    </ListState>
  );
}

// --- Componente principal ----------------------------------------------------

export default function PatientDetailCard({ patient, year = currentYear() }) {
  const [tab, setTab] = useState(0);
  const [memberships, setMemberships] = useState({ items: [], count: 0, loading: true, error: null });
  const [careGaps, setCareGaps] = useState({ items: [], count: 0, loading: true, error: null });
  const [codingOps, setCodingOps] = useState({ items: [], count: 0, loading: true, error: null });

  const cipId = patient?.cipId;
  const active = isPatientActive(patient);

  // Carga care gaps + coding opportunities al cambiar de paciente / año.
  useEffect(() => {
    if (!cipId) return;
    let cancelled = false;

    setMemberships({ items: [], count: 0, loading: true, error: null });
    setCareGaps({ items: [], count: 0, loading: true, error: null });
    setCodingOps({ items: [], count: 0, loading: true, error: null });

    const errMsg = (err) =>
      err?.response?.status === 401
        ? "Your session expired. Please sign in again."
        : "Could not load data. Please try again.";

    getPatientMemberships({ cipId })
      .then(({ items, count }) => {
        if (!cancelled) setMemberships({ items, count, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setMemberships({ items: [], count: 0, loading: false, error: errMsg(err) });
      });

    getPatientCareGaps({ cipId, year })
      .then(({ items, count }) => {
        if (!cancelled) setCareGaps({ items, count, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setCareGaps({ items: [], count: 0, loading: false, error: errMsg(err) });
      });

    // Coding opportunities: SIN filtro de año (su measureYear no coincide con el
    // año calendario; se muestra el measureYear por item).
    getPatientCodingOpportunities({ cipId })
      .then(({ items, count }) => {
        if (!cancelled) setCodingOps({ items, count, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setCodingOps({ items: [], count: 0, loading: false, error: errMsg(err) });
      });

    return () => {
      cancelled = true;
    };
  }, [cipId, year]);

  if (!patient) return null;

  // Tono rojizo de aviso cuando el paciente está inactivo.
  const accent = active
    ? { borderColor: "divider", bgcolor: "background.paper" }
    : { borderColor: "error.light", bgcolor: "rgba(211, 47, 47, 0.06)" };

  return (
    <Paper variant="outlined" sx={{ mt: 1.5, borderRadius: 2, overflow: "hidden", ...accent }}>
      {/* Cabecera con nombre + estado */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.75, py: 1.25, flexWrap: "wrap" }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
          {safe(patient.memberFullName)}
        </Typography>
        <Chip
          size="small"
          icon={active ? <CheckCircleOutlineIcon /> : <PersonOffOutlinedIcon />}
          label={active ? "Active" : "Inactive"}
          color={active ? "success" : "error"}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        />
        <Typography variant="caption" color="text.secondary">
          Year {year}
        </Typography>
      </Stack>

      <Divider sx={{ borderColor: active ? "divider" : "error.light" }} />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 40, px: 0.5, "& .MuiTab-root": { minHeight: 40, textTransform: "none" } }}
      >
        <Tab label="Demographics" />
        <Tab
          sx={{ pr: 3.5 }}
          label={
            <Badge
              color="primary"
              badgeContent={memberships.loading ? "…" : memberships.count}
              showZero
              sx={{ "& .MuiBadge-badge": { right: -16, top: 4 } }}
            >
              <span>Membership</span>
            </Badge>
          }
        />
        <Tab
          sx={{ pr: 3.5 }}
          label={
            <Badge
              color="warning"
              badgeContent={careGaps.loading ? "…" : careGaps.count}
              showZero
              sx={{ "& .MuiBadge-badge": { right: -16, top: 4 } }}
            >
              <span>Care Gaps</span>
            </Badge>
          }
        />
        <Tab
          sx={{ pr: 3.5 }}
          label={
            <Badge
              color="info"
              badgeContent={codingOps.loading ? "…" : codingOps.count}
              showZero
              sx={{ "& .MuiBadge-badge": { right: -16, top: 4 } }}
            >
              <span>Coding Opportunities</span>
            </Badge>
          }
        />
      </Tabs>

      <Divider />

      <Box sx={{ p: 1.75 }}>
        {tab === 0 && <DemographicsTab patient={patient} />}
        {tab === 1 && <MembershipTab state={memberships} />}
        {tab === 2 && <CareGapsTab state={careGaps} year={year} />}
        {tab === 3 && <CodingOppsTab state={codingOps} />}
      </Box>
    </Paper>
  );
}
